import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { dashboardStats, recentJobs, licenseesList } from '../../mocks/dashboard';

export default function DashboardPage() {
  const [activeTab, setActiveTab] = useState<'jobs' | 'licensees' | 'customers'>('jobs');
  const [selectedJob, setSelectedJob] = useState<string | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'In Progress':
        return 'bg-amber-100 text-amber-700';
      case 'Completed':
        return 'bg-green-100 text-green-700';
      case 'Quoted':
        return 'bg-blue-100 text-blue-700';
      case 'Pending':
        return 'bg-gray-100 text-gray-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-24 pb-12">
        <div className="max-w-7xl mx-auto px-6">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-black text-gray-900 mb-2">Dashboard</h1>
            <p className="text-gray-600 text-sm">Manage your demolition business operations</p>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-12">
            {dashboardStats.map((stat) => (
              <div key={stat.id} className="bg-white rounded-xl p-6 shadow-md hover:shadow-lg transition-shadow">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-gray-600 text-xs font-medium">{stat.title}</span>
                  <span
                    className={`text-xs font-semibold ${
                      stat.trend === 'up' ? 'text-green-600' : 'text-amber-600'
                    }`}
                  >
                    {stat.change}
                  </span>
                </div>
                <div className="text-4xl font-black text-gray-900">{stat.value}</div>
              </div>
            ))}
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            {/* Tabs */}
            <div className="border-b border-gray-200">
              <div className="flex">
                <button
                  onClick={() => setActiveTab('jobs')}
                  className={`px-8 py-4 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === 'jobs'
                      ? 'text-teal-600 border-b-2 border-teal-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className="ri-briefcase-line mr-2"></i>
                  Jobs
                </button>
                <button
                  onClick={() => setActiveTab('licensees')}
                  className={`px-8 py-4 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === 'licensees'
                      ? 'text-teal-600 border-b-2 border-teal-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className="ri-team-line mr-2"></i>
                  Licensees
                </button>
                <button
                  onClick={() => setActiveTab('customers')}
                  className={`px-8 py-4 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${
                    activeTab === 'customers'
                      ? 'text-teal-600 border-b-2 border-teal-600'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  <i className="ri-user-line mr-2"></i>
                  Customers
                </button>
              </div>
            </div>

            {/* Jobs Tab */}
            {activeTab === 'jobs' && (
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Recent Jobs</h2>
                  <button className="px-5 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line mr-2"></i>
                    New Job
                  </button>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Job ID</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Customer</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Address</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Type</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Status</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Assigned To</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Value</th>
                        <th className="text-left py-3 px-4 text-xs font-semibold text-gray-600 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentJobs.map((job) => (
                        <tr key={job.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="py-4 px-4 text-sm font-medium text-gray-900">{job.id}</td>
                          <td className="py-4 px-4 text-sm text-gray-700">{job.customer}</td>
                          <td className="py-4 px-4 text-sm text-gray-700">{job.address}</td>
                          <td className="py-4 px-4 text-sm text-gray-700">{job.type}</td>
                          <td className="py-4 px-4">
                            <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(job.status)}`}>
                              {job.status}
                            </span>
                          </td>
                          <td className="py-4 px-4 text-sm text-gray-700">{job.assignedTo}</td>
                          <td className="py-4 px-4 text-sm font-semibold text-gray-900">{job.value}</td>
                          <td className="py-4 px-4">
                            <button
                              onClick={() => setSelectedJob(selectedJob === job.id ? null : job.id)}
                              className="text-teal-600 hover:text-teal-700 cursor-pointer"
                            >
                              <i className="ri-more-2-fill text-lg"></i>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Licensees Tab */}
            {activeTab === 'licensees' && (
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Licensed Contractors</h2>
                  <button className="px-5 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line mr-2"></i>
                    Add Licensee
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {licenseesList.map((licensee) => (
                    <div key={licensee.id} className="bg-gray-50 rounded-xl p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-4">
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-1">{licensee.company}</h3>
                          <p className="text-sm text-gray-600">{licensee.id}</p>
                        </div>
                        <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
                          {licensee.status}
                        </span>
                      </div>

                      <div className="space-y-2 mb-4">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <i className="ri-user-line text-gray-400"></i>
                          {licensee.contact}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <i className="ri-mail-line text-gray-400"></i>
                          {licensee.email}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <i className="ri-phone-line text-gray-400"></i>
                          {licensee.phone}
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        <div className="flex items-center gap-1">
                          <i className="ri-star-fill text-amber-500 text-sm"></i>
                          <span className="text-sm font-semibold text-gray-900">{licensee.rating}</span>
                        </div>
                        <span className="text-sm text-gray-600">{licensee.completedJobs} jobs completed</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Customers Tab */}
            {activeTab === 'customers' && (
              <div className="p-8">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Customer Database</h2>
                  <button className="px-5 py-2 bg-teal-600 text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition-all cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line mr-2"></i>
                    Add Customer
                  </button>
                </div>

                <div className="space-y-4">
                  {recentJobs.map((job) => (
                    <div key={job.id} className="bg-gray-50 rounded-xl p-6 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-gray-900 mb-2">{job.customer}</h3>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <i className="ri-map-pin-line text-gray-400"></i>
                              {job.address}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-gray-700">
                              <i className="ri-file-list-line text-gray-400"></i>
                              Project: {job.type} - {job.status}
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-black text-gray-900 mb-1">{job.value}</div>
                          <div className="text-xs text-gray-500">{job.date}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}